import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type Step = "select" | "configure" | "review";

const SELECT_PARAM = "select";
const IMPORT_PARAM = "import";
const PRODUCTS_PARAM = "products";

export interface ImportModalUrlState {
  /** Whether the import modal should be open (derived from the URL). */
  isOpen: boolean;
  /** Step to open at: `configure` when `?import=true`, otherwise `select`. */
  initialStep: "select" | "configure";
  /** Product ids to pre-select / seed the configure step from (`?products=`). */
  initialSelectedProductIds: string[];
  /** Open the modal at the select step (`?select=true`). */
  openSelect: () => void;
  /** Close the modal — strips all import params, keeping unrelated ones. */
  close: () => void;
  /** Reflect the modal's current step + selection into the URL. */
  syncFromModal: (state: { step: Step; productIds: string[] }) => void;
}

/**
 * Two-way sync between the Shopify import modal's location in the flow and the
 * URL query string, so a hard refresh (or a shared link) restores the roaster
 * to where they were (§11):
 *
 *   select step    → ?select=true
 *   configure step → ?import=true&products=<id1>,<id2>,…
 *
 * The review step is not persisted separately — it maps back to the import URL.
 * The modal stays router-agnostic; this hook (used by the parent view) owns all
 * URL access.
 */
export function useImportModalUrlState(): ImportModalUrlState {
  const [searchParams, setSearchParams] = useSearchParams();

  const isImport = searchParams.get(IMPORT_PARAM) === "true";
  const isSelect = searchParams.get(SELECT_PARAM) === "true";
  const productsRaw = searchParams.get(PRODUCTS_PARAM);

  const initialSelectedProductIds = useMemo(
    () =>
      productsRaw
        ? productsRaw
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        : [],
    [productsRaw]
  );

  const apply = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(SELECT_PARAM);
          next.delete(IMPORT_PARAM);
          next.delete(PRODUCTS_PARAM);
          mutate(next);
          // Avoid redundant navigations when nothing actually changed.
          return next.toString() === prev.toString() ? prev : next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const openSelect = useCallback(() => apply((params) => params.set(SELECT_PARAM, "true")), [apply]);

  const close = useCallback(() => apply(() => undefined), [apply]);

  const syncFromModal = useCallback(
    ({ step, productIds }: { step: Step; productIds: string[] }) => {
      apply((params) => {
        if (step === "select") {
          // Products are intentionally not encoded on the select step.
          params.set(SELECT_PARAM, "true");
          return;
        }
        // configure + review both map to the import URL.
        params.set(IMPORT_PARAM, "true");
        if (productIds.length > 0) {
          params.set(PRODUCTS_PARAM, productIds.join(","));
        }
      });
    },
    [apply]
  );

  return {
    isOpen: isImport || isSelect,
    initialStep: isImport ? "configure" : "select",
    initialSelectedProductIds,
    openSelect,
    close,
    syncFromModal,
  };
}
